import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect, useDispatch } from 'react-redux';
import { withStyles } from 'theme/withStyles';
import useAtomicFi from 'hooks/useAtomicFi';
import fieldsActions from 'core/fields/actions';
import Spinner from 'common/Spinner';
import Help from 'common/Help';
import commonStyles from 'pages/home/styles';
import Button from 'common/Button';
import ErrorMsg from 'common/ErrorMsg';
import AccountCard from 'containers/components/AccountCard';
import { PRIMARY } from '@glu/buttons-react';

const styles = (theme) => {
  const common = commonStyles(theme);
  return {
    ...common,
    account: {
      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        marginTop: 30,
        width: '100%',
      },
      '& .form-group': {
        '& select': {
          marginTop: 0,
        },
        marginTop: 0,
      },
      marginLeft: 20,
      maxWidth: 300,
    },
    accountSelector: {
      left: 0,
      textAlign: 'left',
    },
    atomicFiBtn: {
      float: 'right',
      textIndent: 0,
    },
    button: {
      '& button': {
        '&:disabled': {
          '& i': {
            cursor: 'not-allowed',
          },
          cursor: 'not-allowed',
        },
        background: 'none',
        border: 'none',
        padding: '0 15px 0 0',
      },
      '& i': {
        cursor: 'pointer',
      },
      maxWidth: 50,
      paddingLeft: 24,
    },
    error: {
      clear: 'both',
    },
    label: {
      flex: 1,
    },

    servicesRow: {
      alignItems: 'center',
      display: 'flex',
      [`@media(max-width: ${theme.screenSizes.mobileLandscapeMax}px)`]: {
        flexWrap: 'wrap',
      },
      justifyContent: 'flex-end',
      marginBottom: 30,
      width: '100%',
    },
  };
};

const fieldName = 'appRequest_atomicfi_directDeposit';

/**
 * Docs and testing credentials can be found at https://docs.atomicfi.com/#payroll-connect
 */
const AtomicFi = ({ classes, data, fields, loading, resources, theme }) => {
  const dispatch = useDispatch();
  const loaded = useAtomicFi();
  const accounts = data[fieldName] || undefined;

  const udpateSwitchAttempts = () => {
    dispatch(fieldsActions.atomicFiUpdateSwitchAttempts());
  };

  const onFinish = () => {
    udpateSwitchAttempts();
  };

  const config = {
    language: 'en',
    onClose: udpateSwitchAttempts,
    onFinish,
    product: 'deposit',
    theme: {
      brandColor: theme.palette.secondary,
      overlayColor: theme.palette.primary,
    },
  };

  const handleDDSwitchClick = () => {
    dispatch(
      fieldsActions.atomicFiGetToken({
        config,
        fieldName,
      }),
    );
  };

  return (
    <>
      {loaded && (
        <>
          <div className={classnames('atomicfi-row', classes.servicesRow)}>
            <div className={classnames('afomicfi-label', classes.label)}>
              {fields[fieldName].label}
              <Help help={fields[fieldName].help} id={`help-${fieldName}`} />
            </div>
            <div className={classnames('atomicfi-account', classes.account)}>
              <Button
                disabled={loading}
                className={classnames(classes.atomicFiBtn, `btn btn-mobile btn-${PRIMARY}`)}
                onClick={handleDDSwitchClick}
                variant={PRIMARY}
              >
                {fields[fieldName].placeholder}
              </Button>
              <ErrorMsg style={{ clear: 'both' }} name={fieldName} />
            </div>
          </div>
          {accounts && fields[fieldName].lookupOrder && (
            <>
              {accounts.length > 0 && <p>{resources.ddsConfiguredAccounts}</p>}
              <div className={classnames('', classes.servicesRow)}>
                <AccountCard fieldName={fieldName} />
              </div>
            </>
          )}
        </>
      )}
      <Spinner loading={!loaded} />
    </>
  );
};

AtomicFi.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  data: PropTypes.shape(),
  fields: PropTypes.shape(),
  loading: PropTypes.bool,
  resources: PropTypes.shape(),
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
    }),
  }).isRequired,
};

AtomicFi.defaultProps = {
  data: undefined,
  fields: undefined,
  loading: undefined,
  resources: undefined,
};

const mapStateToProps = (state, { appRequestId, ...props }) => {
  const { data, fields, home, resources, ui } = state;

  if (appRequestId) {
    return {
      data: home?.[appRequestId]?.services?.data ?? null,
      fields: home?.[appRequestId]?.services?.meta?.fields,
      loading: ui.fieldLoading,
      resources: home?.[appRequestId]?.services?.meta?.resources ?? {},
      ...props,
    };
  }
  return {
    data,
    fields,
    loading: ui.fieldLoading,
    resources,
    ...props,
  };
};

const AtomicFiStyled = withStyles(styles)(AtomicFi);

export { AtomicFiStyled as LookupBase };

export default connect(mapStateToProps)(AtomicFiStyled);
